import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { employeeService } from "../../../services/api";
import { pwLabel } from "./policyWizardFieldStyles";

const MIN_QUERY_LEN = 2;

export function extractEmployeesFromResponse(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.data)) return resp.data;
  if (Array.isArray(resp.employees)) return resp.employees;
  if (resp.success && Array.isArray(resp.data)) return resp.data;
  if (resp.data && Array.isArray(resp.data.employees)) return resp.data.employees;
  if (resp.data && Array.isArray(resp.data.data)) return resp.data.data;
  return [];
}

export function normalizeEmployeeOption(employee) {
  const id = employee?.id ?? employee?.employeeId ?? null;
  if (id == null || id === "") return null;
  const name = employee?.user?.name || employee?.name || "Unknown";
  const empCode = employee?.empCode || "";
  const email = employee?.user?.email || employee?.email || "";
  return {
    id: String(id),
    name: String(name),
    empCode: String(empCode || ""),
    email: String(email || ""),
  };
}

export function formatEmployeeChipLabel(emp) {
  const codePart = emp?.empCode ? `${emp.empCode} - ` : "";
  return `${codePart}${emp?.name || "Unknown"}`;
}

export function formatEmployeeOptionLabel(emp) {
  const codePart = emp?.empCode ? `${emp.empCode} - ` : "";
  const emailPart = emp?.email ? ` (${emp.email})` : "";
  return `${codePart}${emp?.name || "Unknown"}${emailPart}`;
}

/**
 * Filters-style multi-select employee picker (chips + typeahead).
 * value: array of { id, name, empCode, email }
 */
export default function EmployeeMultiSelectField({
  darkMode = false,
  value = [],
  onChange,
  disabled = false,
  label = "Individual employees (optional)",
  helpText = "Search by employee code, name, or email. Select one or more.",
}) {
  const selected = Array.isArray(value) ? value.map(normalizeEmployeeOption).filter(Boolean) : [];
  const selectedIds = new Set(selected.map((e) => String(e.id)));

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = String(query || "").trim();
    if (disabled || q.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      if (q.length < MIN_QUERY_LEN) setOpen(false);
      return undefined;
    }
    setOpen(true);
    setActiveIndex(-1);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        let response = await employeeService.getEmployees({
          search: q,
          page: 1,
          limit: 20,
        });
        let all = extractEmployeesFromResponse(response)
          .map(normalizeEmployeeOption)
          .filter(Boolean);
        if (all.length === 0) {
          response = await employeeService.getEmployees({ page: 1, limit: 200 });
          all = extractEmployeesFromResponse(response)
            .map(normalizeEmployeeOption)
            .filter(Boolean);
        }
        const queryLower = q.toLowerCase();
        const filtered = all.filter((emp) => {
          if (selectedIds.has(String(emp.id))) return false;
          const haystack = `${emp.name} ${emp.empCode} ${emp.email}`.toLowerCase();
          return haystack.includes(queryLower);
        });
        setSuggestions(filtered);
        setActiveIndex(filtered.length ? 0 : -1);
      } catch (error) {
        console.error("Employee search failed:", error);
        setSuggestions([]);
        setActiveIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, disabled, selected.map((e) => e.id).join(",")]);

  const emit = (next) => {
    onChange?.(next.map(normalizeEmployeeOption).filter(Boolean));
  };

  const addEmployee = (emp) => {
    const normalized = normalizeEmployeeOption(emp);
    if (!normalized || selectedIds.has(normalized.id)) return;
    emit([...selected, normalized]);
    setQuery("");
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const removeEmployee = (id) => {
    emit(selected.filter((e) => String(e.id) !== String(id)));
  };

  const clearAll = () => emit([]);

  return (
    <div className="mt-3" ref={rootRef}>
      <label className={pwLabel(darkMode)}>{label}</label>
      {helpText ? (
        <p
          className={`mb-2 text-xs ${
            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          {helpText}
        </p>
      ) : null}

      <div
        className={`flex min-h-[44px] w-full flex-wrap items-center gap-2 rounded-md border px-2 py-1 focus-within:ring-2 focus-within:ring-[hsl(var(--ds-primary))]/25 ${
          disabled ? "pointer-events-none opacity-50" : ""
        } ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
      >
        {selected.map((emp) => (
          <span
            key={emp.id}
            className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${
              darkMode
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-emerald-300 bg-emerald-50 text-emerald-700"
            }`}
          >
            <span className="truncate">{formatEmployeeChipLabel(emp)}</span>
            <button
              type="button"
              aria-label={`Remove ${formatEmployeeChipLabel(emp)}`}
              onClick={() => removeEmployee(emp.id)}
              className={`ml-1 rounded-full px-1 transition ${
                darkMode ? "hover:bg-emerald-400/20" : "hover:bg-emerald-100"
              }`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (String(query).trim().length >= MIN_QUERY_LEN) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setActiveIndex(-1);
            }
            if (e.key === "ArrowDown" && open && suggestions.length) {
              e.preventDefault();
              setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
            }
            if (e.key === "ArrowUp" && open && suggestions.length) {
              e.preventDefault();
              setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
            }
            if (e.key === "Enter" && open && suggestions.length) {
              e.preventDefault();
              const index = activeIndex >= 0 ? activeIndex : 0;
              addEmployee(suggestions[index]);
            }
            if (e.key === "Backspace" && !query && selected.length) {
              removeEmployee(selected[selected.length - 1].id);
            }
          }}
          placeholder="Type employee code, name, or email"
          className={`min-w-[240px] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none ${
            darkMode
              ? "text-[hsl(var(--ds-fg))] placeholder:text-[hsl(var(--ds-muted-fg))]"
              : "text-[hsl(var(--ds-fg))]"
          }`}
        />
      </div>

      <p
        className={`mt-1 text-xs ${
          darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        Minimum {MIN_QUERY_LEN} characters. Press Enter to quickly select first result.
      </p>

      {selected.length > 0 && !disabled ? (
        <button
          type="button"
          onClick={clearAll}
          className={`mt-1 text-xs underline ${
            darkMode
              ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
              : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
          }`}
        >
          Clear selected employees
        </button>
      ) : null}

      {open && (
        <div
          className={`relative z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border shadow-lg ${
            darkMode
              ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
          }`}
        >
          {loading ? (
            <div
              className={`px-3 py-2 text-sm ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              Searching employees...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((emp, index) => (
              <button
                key={emp.id}
                type="button"
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => addEmployee(emp)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  index === activeIndex
                    ? darkMode
                      ? "bg-[hsl(var(--ds-muted))]"
                      : "bg-[hsl(var(--ds-muted))]"
                    : ""
                } ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}
              >
                {formatEmployeeOptionLabel(emp)}
              </button>
            ))
          ) : (
            <div
              className={`px-3 py-2 text-sm ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              No matching employees
            </div>
          )}
        </div>
      )}

      {selected.length > 0 ? (
        <p
          className={`mt-2 text-xs font-medium ${
            darkMode ? "text-emerald-300" : "text-emerald-700"
          }`}
        >
          {selected.length} employee{selected.length === 1 ? "" : "s"} selected
        </p>
      ) : null}
    </div>
  );
}
